import {
    Button,
    Checkbox,
    Group,
    Menu,
    Popover,
    Select,
    Stack,
    Text,
    Tooltip,
    ActionIcon,
} from '@mantine/core';
import {
    Rows,
    ArrowsClockwise,
    CaretDown,
    Package,
    Plus,
    WarningCircle,
} from '@phosphor-icons/react';
import { useEffect, useMemo, useState, type FC } from 'react';

import {
    getChannelHealth,
    getChannelWarehouseOptions,
    getDistributionPlanList,
    type DistributionChannelHealth,
    type DistributionSkuHealth,
    type DistributionPriority,
    type DistributionPlanDetail,
    type DistributionPlanSummary,
} from '@api/distribution';
import type { RateBasis } from '@api/inventory';
import StatCard from '@components/ui/StatCard';
import DataSheet, {
    type DataSheetColumn,
    type DataSheetFilterConfig,
} from '@components/ui/DataSheet';
import type { FilterValue } from '@components/ui/EntityFilterDropdown';
import { RateBasisControl } from '@components/ui/RateBasisControl';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useProductFilterParams } from '@hooks/useProductFilterParams';
import { useRatePolicy } from '@hooks/useRatePolicy';
import { neutralColors, semanticColors } from '@styles/design-system';

import { WHDemandOverrideModal } from './WHDemandOverrideModal';
import styles from './ChannelDetailView.module.css';
import { formatCount } from '@utils/currency';

// ─── Types ─────────────────────────────────────────────────────────────────────

type DemandSource = 'FORECAST' | 'PAST_SALES' | 'OPEN_PO';

const PRIORITY_COLOR: Record<DistributionPriority, string> = {
    CRITICAL: semanticColors.critical.primary,
    MEDIUM: semanticColors.warning.primary,
    LOW: semanticColors.positive.primary,
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function formatUnits(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return formatCount(n);
}

function formatDoh(days: number): string {
    if (days >= 999) return '—';
    return `${Math.floor(days)}d`;
}

// ─── Column toggle ─────────────────────────────────────────────────────────────

const CHANNEL_OPTIONAL_COLS = [
    { key: 'inTransit', label: 'In-Transit' },
    { key: 'currentDrr', label: 'DRR' },
    { key: 'currentDohDays', label: 'DoH' },
    { key: 'gapUnits', label: 'Gap Units' },
    { key: 'priority', label: 'Priority' },
];

function ChannelColumnToggle({
    hidden,
    onChange,
}: {
    hidden: Set<string>;
    onChange: (h: Set<string>) => void;
}) {
    const toggle = (key: string) => {
        const next = new Set(hidden);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        onChange(next);
    };
    return (
        <Popover position="bottom-end" withArrow shadow="md" withinPortal>
            <Popover.Target>
                <Tooltip label="Show / hide columns" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm">
                        <Rows size={15} />
                    </ActionIcon>
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap={6} p={4}>
                    <Text
                        style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#888',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                        }}
                    >
                        Columns
                    </Text>
                    {CHANNEL_OPTIONAL_COLS.map((def) => (
                        <Checkbox
                            key={def.key}
                            label={def.label}
                            size="xs"
                            checked={!hidden.has(def.key)}
                            onChange={() => toggle(def.key)}
                        />
                    ))}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
}

// ─── SKU table columns ─────────────────────────────────────────────────────────

interface BuildColumnsOpts {
    hidden?: Set<string>;
    hasForecast: boolean;
    minProcessDays: number;
    maxProcessDays: number;
    drrLookbackDays?: number;
    rateBasis?: RateBasis;
}

/** Returns all columns (including optional ones) without applying hidden filter. Used for CSV download. */
function buildAllColumns({
    hasForecast,
    minProcessDays,
    maxProcessDays,
    drrLookbackDays,
    rateBasis,
}: Omit<BuildColumnsOpts, 'hidden'>): DataSheetColumn<DistributionSkuHealth>[] {
    return buildColumns({
        hasForecast,
        minProcessDays,
        maxProcessDays,
        drrLookbackDays,
        rateBasis,
    });
}

function buildColumns({
    hidden,
    hasForecast,
    minProcessDays,
    maxProcessDays,
    drrLookbackDays = 30,
    rateBasis = 'BACKWARD',
}: BuildColumnsOpts): DataSheetColumn<DistributionSkuHealth>[] {
    const hasLeadRange = minProcessDays !== maxProcessDays;
    const leadLabel = hasLeadRange
        ? `est. ${minProcessDays}–${maxProcessDays}d lead`
        : `est. ${maxProcessDays}d lead`;

    const all: DataSheetColumn<DistributionSkuHealth>[] = [
        {
            key: '_idx',
            header: '#',
            width: 44,
            sticky: true,
            render: (_, _row, idx) => (
                <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>
                    {idx + 1}
                </Text>
            ),
        },
        {
            key: 'productName',
            header: 'Product',
            width: 220,
            sticky: true,
            sortable: true,
            render: (_, row) => (
                <div>
                    <Text
                        style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: neutralColors.textPrimary,
                        }}
                    >
                        {row.productName || row.productId}
                    </Text>
                    <Text
                        style={{ fontSize: 11, color: neutralColors.textMuted }}
                    >
                        {row.productId}
                    </Text>
                </div>
            ),
        },
        {
            key: 'channelStock',
            header: 'Channel Stock',
            width: 110,
            align: 'right',
            sortable: true,
            render: (_, row) => (
                <Text
                    style={{ fontSize: 13, color: neutralColors.textPrimary }}
                >
                    {formatUnits(Math.ceil(row.channelStock))}
                </Text>
            ),
        },
        {
            key: 'internalAvailableUnits',
            header: 'Internal Stock',
            width: 110,
            align: 'right',
            sortable: true,
            render: (_, row) => (
                <Text
                    style={{
                        fontSize: 13,
                        color:
                            row.internalAvailableUnits > 0
                                ? neutralColors.textPrimary
                                : neutralColors.textMuted,
                    }}
                >
                    {row.internalAvailableUnits > 0
                        ? formatUnits(Math.ceil(row.internalAvailableUnits))
                        : '—'}
                </Text>
            ),
        },
        {
            key: 'inTransit',
            header: 'In-Transit',
            width: 100,
            align: 'right',
            sortable: true,
            render: (_, row) => (
                <Text
                    style={{
                        fontSize: 13,
                        color:
                            row.inTransit > 0
                                ? semanticColors.info.primary
                                : neutralColors.textMuted,
                    }}
                >
                    {row.inTransit > 0
                        ? formatUnits(Math.ceil(row.inTransit))
                        : '—'}
                </Text>
            ),
        },
        {
            // The rate DoH divides by, under the viewer's basis — not always a run rate, so the
            // header follows the basis rather than always claiming "DRR".
            key: 'effectiveDailyRate',
            header:
                rateBasis === 'FORWARD'
                    ? 'Forecast rate'
                    : `${drrLookbackDays}-day DRR`,
            width: 100,
            align: 'right',
            sortable: true,
            render: (_, row) => {
                const value = row.effectiveDailyRate ?? row.currentDrr;
                return (
                    <Text
                        style={{
                            fontSize: 13,
                            color: neutralColors.textSecondary,
                        }}
                    >
                        {value > 0 ? `${value.toFixed(1)}/d` : '—'}
                    </Text>
                );
            },
        },
        {
            key: 'currentDohDays',
            header: 'DoH',
            width: 88,
            align: 'right',
            sortable: true,
            render: (_, row) => (
                <Text
                    style={{ fontSize: 13, color: neutralColors.textSecondary }}
                >
                    {formatDoh(row.currentDohDays)}
                </Text>
            ),
        },
        {
            key: 'gapUnits',
            header: 'Gap Units',
            width: 110,
            align: 'right',
            sortable: true,
            render: (_, row) => (
                <Text
                    style={{
                        fontSize: 13,
                        fontWeight: row.gapUnits > 0 ? 600 : 400,
                        color:
                            row.gapUnits > 0
                                ? semanticColors.critical.primary
                                : neutralColors.textMuted,
                    }}
                >
                    {row.gapUnits > 0
                        ? formatUnits(Math.ceil(row.gapUnits))
                        : '—'}
                </Text>
            ),
        },
        // Forecasted DRR — forecast demand normalised to a daily rate; only shown when forecast data exists
        ...(hasForecast
            ? [
                  {
                      key: 'forecastedDrr',
                      header: 'Forecasted DRR',
                      width: 130,
                      align: 'right' as const,
                      sortable: true,
                      renderHeader: () => (
                          <div style={{ textAlign: 'right' }}>
                              <Text
                                  style={{
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: neutralColors.textSecondary,
                                  }}
                              >
                                  Forecasted DRR
                              </Text>
                              <Text
                                  style={{
                                      fontSize: 11,
                                      color: neutralColors.textMuted,
                                  }}
                              >
                                  {leadLabel}
                              </Text>
                          </div>
                      ),
                      render: (_: unknown, row: DistributionSkuHealth) => {
                          if (
                              row.forecastDemandHigh === null ||
                              row.targetDohDays === 0
                          ) {
                              return (
                                  <Text
                                      style={{
                                          fontSize: 13,
                                          color: neutralColors.textMuted,
                                      }}
                                  >
                                      —
                                  </Text>
                              );
                          }
                          const drr =
                              row.forecastDemandHigh / row.targetDohDays;
                          return (
                              <Text
                                  style={{
                                      fontSize: 13,
                                      color: neutralColors.textSecondary,
                                  }}
                              >
                                  {`${drr.toFixed(1)}/d`}
                              </Text>
                          );
                      },
                  } satisfies DataSheetColumn<DistributionSkuHealth>,
              ]
            : []),
        {
            key: 'priority',
            header: 'Priority',
            width: 90,
            align: 'center',
            sortable: true,
            render: (_, row) => {
                if (!row.priority) {
                    return (
                        <Text
                            style={{
                                fontSize: 12,
                                color: neutralColors.textDisabled,
                            }}
                        >
                            —
                        </Text>
                    );
                }
                const color = PRIORITY_COLOR[row.priority];
                return (
                    <Group gap={4} justify="center" wrap="nowrap">
                        <span
                            style={{
                                display: 'inline-block',
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                backgroundColor: color,
                                flexShrink: 0,
                            }}
                        />
                        <Text
                            style={{
                                fontSize: 11,
                                color,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {row.priority}
                        </Text>
                    </Group>
                );
            },
        },
    ];
    return hidden ? all.filter((c) => !hidden.has(c.key)) : all;
}

// ─── Main component ────────────────────────────────────────────────────────────

export interface ChannelDetailViewProps {
    /** Initial channel data (already loaded by the overview call — no extra fetch needed). */
    initialData: DistributionChannelHealth;
    onBack: () => void;
    onComputePlan: (params: {
        demandSource: DemandSource;
        targetDohDays: number | null;
    }) => void;
    onOpenPlan?: (planId: string, planName?: string) => void;
    onPlanComputed?: (plan: DistributionPlanDetail) => void;
}

const ChannelDetailView: FC<ChannelDetailViewProps> = ({
    initialData,
    onBack,
    onComputePlan,
    onOpenPlan,
}) => {
    const channelAbbreviationKey = initialData.channelAbbreviationKey;
    const [channelData, setChannelData] =
        useState<DistributionChannelHealth>(initialData);
    const [selectedWarehouseId, setSelectedWarehouseId] = useState<
        string | null
    >(null);
    const [channelWarehouses, setChannelWarehouses] = useState<
        Array<{ warehouseId: string; name: string }>
    >([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [draftPlans, setDraftPlans] = useState<DistributionPlanSummary[]>([]);
    const [draftPlansLoading, setDraftPlansLoading] = useState(true);
    // Table filtering state
    const [searchValue, setSearchValue] = useState('');
    const [priorityFilter, setPriorityFilter] = useState<string[]>([]);

    const { currentUser } = useCurrentUser();
    const rate = useRatePolicy(currentUser?.organizationId);
    const { effectiveBasis, effectiveWindow, setSaved } = rate;

    const fetchData = (whId?: string | null) => {
        setLoading(true);
        setError(false);
        const resolvedWhId = whId !== undefined ? whId : selectedWarehouseId;
        getChannelHealth({
            channelKey: channelAbbreviationKey,
            warehouseId: resolvedWhId ?? undefined,
            basis: effectiveBasis,
            drrLookbackDays:
                effectiveBasis === 'BACKWARD' ? effectiveWindow : undefined,
        })
            .then((d) => {
                setChannelData(d.channels[0] ?? channelData);
                if (d.savedRateBasis && d.savedDrrWindowDays != null) {
                    setSaved(d.savedRateBasis, d.savedDrrWindowDays);
                }
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        getChannelWarehouseOptions(channelAbbreviationKey)
            .then((opts) => setChannelWarehouses(opts.channelWarehouses))
            .catch(() => setChannelWarehouses([]));
        getDistributionPlanList(20)
            .then((all) =>
                setDraftPlans(
                    all.filter(
                        (p) =>
                            p.status === 'DRAFT' &&
                            p.channelAbbreviationKey === channelAbbreviationKey,
                    ),
                ),
            )
            .catch(() => setDraftPlans([]))
            .finally(() => setDraftPlansLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Re-fetch when the warehouse selection or the rate basis/window changes.
    useEffect(() => {
        fetchData();
    }, [selectedWarehouseId, effectiveBasis, effectiveWindow]); // eslint-disable-line react-hooks/exhaustive-deps

    const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(
        new Set(['gapUnits']),
    );
    const [whDemandModalOpen, setWhDemandModalOpen] = useState(false);

    const skus = channelData.skus;
    const isNonAggregated = !channelData.isAggregated;
    const hasForecast = channelData.hasForecast;
    const minProcessDays = channelData.minProcessDays;
    const maxProcessDays = channelData.maxProcessDays;
    const drrLookbackDays = channelData.drrLookbackDays;
    const columns = buildColumns({
        hidden: hiddenColumns,
        hasForecast,
        minProcessDays,
        maxProcessDays,
        drrLookbackDays,
        rateBasis: effectiveBasis,
    });
    const allColumns = buildAllColumns({
        hasForecast,
        minProcessDays,
        maxProcessDays,
        drrLookbackDays,
        rateBasis: effectiveBasis,
    });

    // Filtered skus for table
    const filteredSkus = useMemo(() => {
        let result = skus;
        if (searchValue.trim()) {
            const q = searchValue.toLowerCase();
            result = result.filter(
                (s) =>
                    s.productName?.toLowerCase().includes(q) ||
                    s.productId.toLowerCase().includes(q),
            );
        }
        if (priorityFilter.length > 0) {
            result = result.filter(
                (s) => s.priority && priorityFilter.includes(s.priority),
            );
        }
        return result;
    }, [skus, searchValue, priorityFilter]);

    // Product filter — derived from loaded skus
    const [productFilter, setProductFilter] = useState<FilterValue>({});
    const categoryOptions = useMemo(
        () =>
            [
                ...new Set(
                    skus.map((s) => s.category).filter((c): c is string => !!c),
                ),
            ]
                .sort()
                .map((c) => ({ label: c, value: c })),
        [skus],
    );
    const productGroupOptions = useMemo(
        () =>
            [
                ...new Set(
                    skus
                        .map((s) => s.productGroup)
                        .filter((g): g is string => !!g),
                ),
            ]
                .sort()
                .map((g) => ({ label: g, value: g })),
        [skus],
    );
    const tierOptions = useMemo(
        () =>
            [
                ...new Set(
                    skus.map((s) => s.tier).filter((t): t is string => !!t),
                ),
            ]
                .sort()
                .map((t) => ({ label: t, value: t })),
        [skus],
    );
    const { params: productFilterParams, resolvedAttrIds } =
        useProductFilterParams(
            { categoryOptions, productGroupOptions, tierOptions },
            productFilter,
        );
    const visibleSkus = useMemo(() => {
        let result = filteredSkus;
        if (productFilter.category?.length)
            result = result.filter((s) =>
                productFilter.category!.includes(s.category ?? ''),
            );
        if (productFilter.productGroup?.length)
            result = result.filter((s) =>
                productFilter.productGroup!.includes(s.productGroup ?? ''),
            );
        if (productFilter.tier?.length)
            result = result.filter((s) =>
                productFilter.tier!.includes(s.tier ?? ''),
            );
        if (productFilter.isActive?.length) {
            const wantActive = productFilter.isActive[0] === 'true';
            result = result.filter((s) => s.isActive === wantActive);
        }
        if (resolvedAttrIds)
            result = result.filter((s) => resolvedAttrIds.has(s.productId));
        return result;
    }, [filteredSkus, productFilter, resolvedAttrIds]);

    // Stat card values
    const totalChannelStock = skus.reduce((s, r) => s + r.channelStock, 0);
    const totalInternalAvail = skus.reduce(
        (s, r) => s + r.internalAvailableUnits,
        0,
    );
    const criticalCount = skus.filter((s) => s.priority === 'CRITICAL').length;
    const mediumCount = skus.filter((s) => s.priority === 'MEDIUM').length;
    const deficitCount = criticalCount + mediumCount;

    // DataSheet filters
    const dsFilters: DataSheetFilterConfig[] = [
        {
            label: 'Priority',
            params: [
                {
                    key: 'priority',
                    label: 'Priority',
                    mode: 'multi',
                    options: [
                        { value: 'CRITICAL', label: 'Critical' },
                        { value: 'MEDIUM', label: 'Medium' },
                        { value: 'LOW', label: 'Low' },
                    ],
                },
            ],
            value: { priority: priorityFilter },
            onChange: (v) => setPriorityFilter((v.priority as string[]) ?? []),
        },
        {
            label: 'Product',
            params: productFilterParams,
            value: productFilter,
            onChange: setProductFilter,
        },
    ];

    return (
        <div className={styles.root}>
            {/* Header */}
            <Group justify="space-between" pb="md" style={{ flexShrink: 0 }}>
                <Group gap={8}>
                    <Button
                        variant="subtle"
                        color="dark"
                        size="xs"
                        onClick={onBack}
                    >
                        ← Overview
                    </Button>
                    <Text
                        style={{ fontSize: 13, color: neutralColors.textMuted }}
                    >
                        /
                    </Text>
                    <Text
                        style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: neutralColors.textPrimary,
                        }}
                    >
                        {channelData.channelName}
                    </Text>
                    {!loading && deficitCount > 0 && (
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                color: semanticColors.critical.primary,
                                backgroundColor: semanticColors.critical.light,
                                borderRadius: 4,
                                padding: '2px 8px',
                            }}
                        >
                            {deficitCount} SKU{deficitCount !== 1 ? 's' : ''}{' '}
                            below target
                        </span>
                    )}
                </Group>

                <Group gap={8}>
                    <Button
                        variant="subtle"
                        color="dark"
                        size="xs"
                        leftSection={<ArrowsClockwise size={14} />}
                        onClick={() => fetchData()}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                    {isNonAggregated && channelWarehouses.length > 0 && (
                        <Button
                            variant="light"
                            color="indigo"
                            size="xs"
                            onClick={() => setWhDemandModalOpen(true)}
                        >
                            WH Demand
                        </Button>
                    )}
                    <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                            <Button
                                size="sm"
                                rightSection={<CaretDown size={14} />}
                                disabled={loading}
                            >
                                Compute Plan
                            </Button>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item
                                leftSection={<Plus size={14} />}
                                onClick={() =>
                                    onComputePlan({
                                        demandSource:
                                            'FORECAST' as DemandSource,
                                        targetDohDays:
                                            channelData.targetDohDays ?? null,
                                    })
                                }
                            >
                                New Plan
                            </Menu.Item>
                            {!draftPlansLoading &&
                                draftPlans.length > 0 &&
                                onOpenPlan && (
                                    <>
                                        <Menu.Divider />
                                        <Menu.Label>
                                            Continue working
                                        </Menu.Label>
                                        {draftPlans.map((p) => (
                                            <Menu.Item
                                                key={p.id}
                                                onClick={() =>
                                                    onOpenPlan(p.id, p.name)
                                                }
                                            >
                                                <Text style={{ fontSize: 13 }}>
                                                    {p.name}
                                                </Text>
                                                <Text
                                                    style={{
                                                        fontSize: 11,
                                                        color: neutralColors.textMuted,
                                                    }}
                                                >
                                                    {p.lineCount} lines
                                                </Text>
                                            </Menu.Item>
                                        ))}
                                    </>
                                )}
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Group>

            {/* Stat cards */}
            {!error && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 12,
                        marginBottom: 16,
                        flexShrink: 0,
                    }}
                >
                    <StatCard
                        label="Critical priority SKUs"
                        value={String(criticalCount)}
                        helper={`${mediumCount} medium priority`}
                        valueColor={
                            criticalCount > 0
                                ? semanticColors.critical.primary
                                : undefined
                        }
                        loading={loading}
                    />
                    <StatCard
                        label="Channel stock"
                        value={formatUnits(Math.ceil(totalChannelStock))}
                        helper="units in channel warehouses"
                        loading={loading}
                    />
                    <StatCard
                        label="Internal available"
                        value={formatUnits(Math.ceil(totalInternalAvail))}
                        helper="units in internal warehouses"
                        loading={loading}
                    />
                    <StatCard
                        label="Total SKUs"
                        value={String(skus.length)}
                        helper={
                            selectedWarehouseId
                                ? `in ${channelWarehouses.find((w) => w.warehouseId === selectedWarehouseId)?.name ?? 'warehouse'}`
                                : 'active SKUs this channel'
                        }
                        loading={loading}
                    />
                </div>
            )}

            {/* SKU Table */}
            {error ? (
                <Stack
                    align="center"
                    justify="center"
                    style={{ flex: 1 }}
                    gap={8}
                    p="xl"
                >
                    <WarningCircle
                        size={32}
                        color={semanticColors.warning.primary}
                    />
                    <Text
                        style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: neutralColors.textSecondary,
                        }}
                    >
                        Could not load channel detail
                    </Text>
                    <Text
                        style={{
                            fontSize: 13,
                            color: neutralColors.textMuted,
                            textAlign: 'center',
                            maxWidth: 300,
                        }}
                    >
                        The channel detail endpoint is not yet available.
                    </Text>
                    <Button
                        variant="subtle"
                        size="xs"
                        onClick={() => fetchData()}
                    >
                        Retry
                    </Button>
                </Stack>
            ) : (
                <div style={{ flex: 1, minHeight: 0 }}>
                    <DataSheet
                        data={loading ? [] : visibleSkus}
                        columns={columns}
                        allColumns={allColumns}
                        enableDownload
                        downloadFilename={`${channelData.channelName}-sku-detail.csv`}
                        getRowKey={(row) => row.productId}
                        title={`${channelData.channelName} — SKU Detail`}
                        toolbarControls={
                            isNonAggregated && channelWarehouses.length > 0 ? (
                                <Select
                                    size="xs"
                                    placeholder="All FCs"
                                    clearable
                                    data={channelWarehouses.map((w) => ({
                                        value: w.warehouseId,
                                        label: w.name,
                                    }))}
                                    value={selectedWarehouseId}
                                    onChange={setSelectedWarehouseId}
                                    style={{ width: 200 }}
                                />
                            ) : undefined
                        }
                        subtitle={
                            loading
                                ? 'Loading…'
                                : `${visibleSkus.length}${visibleSkus.length !== skus.length ? ` of ${skus.length}` : ''} SKU${skus.length !== 1 ? 's' : ''} · ${deficitCount} below target DoH`
                        }
                        isLoading={loading}
                        enableSearch
                        searchPlaceholder="Search by SKU or name…"
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        filters={dsFilters}
                        toolbarActionsRight={
                            <Group gap={8} wrap="nowrap">
                                <RateBasisControl
                                    metricLabel="DRR"
                                    savedBasis={rate.savedBasis}
                                    effectiveBasis={rate.effectiveBasis}
                                    effectiveWindow={rate.effectiveWindow}
                                    isDirty={rate.isDirty}
                                    saving={rate.saving}
                                    onBasisChange={rate.setBasis}
                                    onWindowChange={rate.setWindow}
                                    onSave={rate.save}
                                    onReset={rate.reset}
                                />
                                <ChannelColumnToggle
                                    hidden={hiddenColumns}
                                    onChange={setHiddenColumns}
                                />
                            </Group>
                        }
                        maxHeight="calc(100vh - 420px)"
                        emptyState={
                            <Stack align="center" gap={8} p="xl">
                                <Package
                                    size={32}
                                    color={neutralColors.textMuted}
                                />
                                <Text
                                    style={{
                                        fontSize: 13,
                                        color: neutralColors.textMuted,
                                    }}
                                >
                                    {skus.length === 0
                                        ? 'No active SKUs for this channel.'
                                        : 'No results match your filter.'}
                                </Text>
                            </Stack>
                        }
                    />
                </div>
            )}

            <WHDemandOverrideModal
                open={whDemandModalOpen}
                onClose={() => setWhDemandModalOpen(false)}
                channelKey={channelAbbreviationKey}
                warehouses={channelWarehouses}
                initialWarehouseId={selectedWarehouseId}
            />
        </div>
    );
};

export default ChannelDetailView;
